import InputAdornment from "@material-ui/core/InputAdornment";
import MuiTextField, { FilledTextFieldProps } from "@material-ui/core/TextField";
import clsx from "clsx";
import React, { useCallback } from "react";
import { useFocus } from "../../hook";
import { HasTestId, PlainFunctionComponent } from "../../types";
import { EndAdornmentContent, EndAdornmentContentProps } from "../EndAdornmentContent";
import { HelperTextWithCounter } from "../HelperTextWithCounter";

export interface TextAreaProps
    extends Omit<FilledTextFieldProps, "color" | "defaultValue" | "multiline" | "onChange" | "type" | "select" | "SelectProps" | "size" | "value" | "variant">,
        HasTestId {
    /**
     * Whether the clear button should be displayed if the field is `focused` and non-empty. Defaults to `undefined` which is interpreted as `false`.
     */
    disableClearButton?: boolean;
    /**
     * Whether the lock icon should be displayed if the field is `disabled`. Defaults to `undefined` which is interpreted as `false`.
     */
    disableLockIcon?: boolean;
    /**
     * Don't display the validation icon. Defaults to `undefined` which is interpreted as `false` (= validation icon is shown if text is not empty depending on `error` property).
     */
    disableValidationIcon?: boolean;
    /**
     * Props applied to the Input element. It will be a `FilledInput` but without `endAdornment` which is customized completely
     */
    InputProps?: Omit<NonNullable<FilledTextFieldProps["InputProps"]>, "endAdornment">;
    /**
     * Every field should have a `label`. Use `""` (= empty string) if really none should be displayed.
     */
    label: FilledTextFieldProps["label"];
    maxLength?: number;
    onChange: (newValue: string | undefined, event?: React.ChangeEvent<HTMLInputElement>) => void;
    /**
     * Every field should have a `placeholder`. Use `""` (= empty string) if really none should be displayed.
     */
    placeholder: FilledTextFieldProps["placeholder"];
    value: string | undefined;
}

export const TextArea: PlainFunctionComponent<TextAreaProps> = React.forwardRef(
    (
        {
            autoFocus,
            disabled,
            disableClearButton,
            disableLockIcon,
            disableValidationIcon,
            error,
            helperText,
            InputProps,
            maxLength,
            onBlur,
            onChange,
            onFocus,
            required,
            rows = 3,
            rowsMax = 10,
            testId,
            value,
            ...props
        },
        ref,
    ) => {
        const { focus, handleBlur, handleFocus } = useFocus(autoFocus, onBlur, onFocus);

        const handleChange = useCallback(
            (event: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = event.target.value;
                onChange(newValue === "" ? undefined : newValue, event);
            },
            [onChange],
        );
        const handleClear = useCallback(() => {
            onChange(undefined);
        }, [onChange]);

        const enhancedInputProps = {
            ...InputProps,
            className: clsx(InputProps?.className, { required }),
            endAdornment: (
                <EndAdornment
                    disabled={disabled}
                    disableClearButton={disableClearButton}
                    disableLockIcon={disableLockIcon}
                    disableValidationIcon={disableValidationIcon}
                    error={error}
                    focus={focus}
                    onClear={handleClear}
                    value={value}
                />
            ),
        };
        return (
            <MuiTextField
                autoFocus={autoFocus}
                data-testid={testId}
                disabled={disabled}
                error={error}
                fullWidth={true}
                helperText={<HelperTextWithCounter disabled={disabled} helperText={helperText} length={value ? value.length : 0} maxLength={maxLength} />}
                InputProps={enhancedInputProps}
                multiline={true}
                onBlur={handleBlur}
                onChange={handleChange}
                onFocus={handleFocus}
                ref={ref}
                required={required}
                rows={rows}
                rowsMax={rowsMax}
                type="text"
                value={value ?? ""} // default to "" because otherwise input thinks to be uncontrolled which causes a thrown exception on console by React
                {...props}
            />
        );
    },
);

const EndAdornment: PlainFunctionComponent<EndAdornmentContentProps> = (props) => (
    <InputAdornment position="end">
        <EndAdornmentContent {...props} />
    </InputAdornment>
);
